import type { z } from "zod";

import {
  cipherDataSchema,
  createApiKeyDataSchema,
  decodeDataSchema,
  deletedCipherDataSchema,
  encodeDataSchema,
  errorBodySchema,
  legacyErrorSchema,
  listApiKeysDataSchema,
  listCiphersDataSchema,
  listVerificationsDataSchema,
  meResponseSchema,
  revokedApiKeyDataSchema,
  structuredErrorSchema,
  successEnvelope,
  verificationDataSchema,
  verificationStreamEventSchema,
  type CipherWithMapping,
  type CreateApiKeyInput,
  type CreateCipherInput,
  type CreateVerificationInput,
  type CreatedApiKey,
  type DecideVerificationInput,
  type DecodeData,
  type DecodeInput,
  type DeletedCipherData,
  type EncodeData,
  type EncodeInput,
  type ErrorCode,
  type ListApiKeysData,
  type ListCiphersData,
  type ListCiphersInput,
  type ListVerificationsData,
  type ListVerificationsInput,
  type MeResponse,
  type PublicApiKey,
  type PublicCipher,
  type RevokedApiKeyData,
  type UpdateCipherInput,
  type Verification,
  type VerificationStreamEvent,
} from "./schemas.js";

export type CryptolectCredentials = { apiKey: string } | undefined;

export type CryptolectFetch = typeof fetch;

// `WebSocketLike` matches the WHATWG WebSocket API surface that
// `streamVerifications` actually depends on — `addEventListener`, `close`,
// and the `readyState` constants. Typed loosely so the same client works
// with `globalThis.WebSocket`, Node's `ws` package, a Workers-pool stub, or
// the test-only `FakeWebSocket` without forcing a single concrete type.
export type WebSocketLike = {
  readonly readyState: number;
  close(code?: number, reason?: string): void;
  addEventListener(type: "open", listener: (event: Event) => void): void;
  addEventListener(
    type: "message",
    listener: (event: MessageEvent) => void,
  ): void;
  addEventListener(type: "close", listener: (event: CloseEvent) => void): void;
  addEventListener(type: "error", listener: (event: Event) => void): void;
};
export type WebSocketConstructor = new (
  url: string,
  protocols?: string | string[],
) => WebSocketLike;

export interface CryptolectApiClientOptions {
  baseUrl: string;
  fetch?: CryptolectFetch;
  // Optional WebSocket constructor for `streamVerifications`. Defaults to
  // `globalThis.WebSocket`; tests inject a fake to drive open/message/close
  // events deterministically.
  webSocket?: WebSocketConstructor;
  // `undefined` → cookie-based session (Better Auth). Returning `{ apiKey }`
  // sends `Authorization: Bearer <apiKey>` instead. The function is awaited so
  // callers can lazily fetch a key from a secret store.
  getCredentials?: () =>
    | CryptolectCredentials
    | Promise<CryptolectCredentials>;
  // Default `credentials` mode forwarded to every fetch call. `"include"` is
  // required for the cookie-based session path when the SDK runs in a browser
  // against a different subdomain (e.g. apps/web on Pages talking to the API
  // Worker). Per-request `init.credentials` overrides this.
  credentials?: RequestCredentials;
}

export interface VerificationStreamReconnectOptions {
  // Initial backoff after an abnormal close. Defaults to 1000ms.
  initialDelayMs?: number;
  // Cap on the doubling backoff. Defaults to 30_000ms.
  maxDelayMs?: number;
  // Cap on consecutive reconnect attempts; `undefined` = retry forever.
  maxAttempts?: number;
}

export interface StreamVerificationsOptions {
  // Override the client-level constructor for this stream only.
  webSocket?: WebSocketConstructor;
  // Surfaced when a frame fails schema validation, when JSON.parse throws,
  // or when the underlying socket emits an `error` event. Reconnect-loop
  // failures are *not* delivered here — the stream just keeps retrying.
  onError?: (error: unknown) => void;
  // Fires each time a new socket opens — useful for resubscribing to UI
  // state after a reconnect.
  onOpen?: () => void;
  // Fires when reconnect attempts are exhausted (`maxAttempts` was set).
  onClosed?: () => void;
  reconnect?: VerificationStreamReconnectOptions;
  // Override globalThis.setTimeout so tests can run reconnects synchronously.
  setTimeout?: (handler: () => void, ms: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
}

export interface VerificationStream {
  // Closes the active socket with code 1000 and prevents any further
  // reconnect attempts. Idempotent.
  close(): void;
  // True after `close()` has been called or after the reconnect loop has
  // exhausted `maxAttempts`.
  readonly closed: boolean;
}

const DEFAULT_RECONNECT: Required<VerificationStreamReconnectOptions> = {
  initialDelayMs: 1_000,
  maxDelayMs: 30_000,
  // `Infinity` instead of leaving `maxAttempts` undefined so the runtime
  // arithmetic in the reconnect loop is uniform.
  maxAttempts: Number.POSITIVE_INFINITY,
};

// Thrown for any non-2xx response. `code` is the stable wire code so callers
// can branch on `err.code === "cipher_not_granted"` instead of parsing
// messages. Legacy 401s (`{ error: "unauthorized" }`) surface as
// `code: "invalid_token"` because the only way that response fires today is
// on a missing/invalid auth credential.
export class CryptolectApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly issues?: unknown[];
  readonly body: unknown;

  constructor(args: {
    code: ErrorCode;
    message: string;
    status: number;
    issues?: unknown[];
    body: unknown;
  }) {
    super(args.message);
    this.name = "CryptolectApiError";
    this.code = args.code;
    this.status = args.status;
    this.issues = args.issues;
    this.body = args.body;
  }
}

type RequestInitEx = RequestInit & {
  // Hono routes accept `Record<string, string>` for query strings; carrying it
  // here so `request()` can append into `URL.searchParams` uniformly.
  query?: Record<string, string | number | undefined>;
};

function joinUrl(baseUrl: string, path: string): string {
  const trimmedBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  const trimmedPath = path.startsWith("/") ? path : `/${path}`;
  return `${trimmedBase}${trimmedPath}`;
}

export class CryptolectApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: CryptolectFetch;
  private readonly webSocketImpl: WebSocketConstructor | undefined;
  private readonly getCredentials: () =>
    | CryptolectCredentials
    | Promise<CryptolectCredentials>;
  private readonly defaultCredentials: RequestCredentials;

  constructor(options: CryptolectApiClientOptions) {
    this.baseUrl = options.baseUrl;
    // `globalThis.fetch` is rebound so it can run in Node, browsers, Workers,
    // and Vitest pools without the caller wiring it themselves.
    this.fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
    // WebSocket is optional — only required when `streamVerifications` is
    // called. `globalThis.WebSocket` is the WHATWG global available in
    // Node 22+, browsers, and Workers; cast through `unknown` because the
    // global's TS type doesn't structurally match `WebSocketLike`.
    this.webSocketImpl =
      options.webSocket ??
      (globalThis as { WebSocket?: WebSocketConstructor }).WebSocket;
    this.getCredentials = options.getCredentials ?? (() => undefined);
    this.defaultCredentials = options.credentials ?? "include";
  }

  private async buildHeaders(extra?: HeadersInit): Promise<Headers> {
    const headers = new Headers(extra);
    const creds = await this.getCredentials();
    if (creds && "apiKey" in creds) {
      headers.set("authorization", `Bearer ${creds.apiKey}`);
    }
    return headers;
  }

  private async request<TSchema extends z.ZodType>(
    method: string,
    path: string,
    schema: TSchema,
    init?: RequestInitEx,
  ): Promise<z.infer<TSchema>> {
    const url = new URL(joinUrl(this.baseUrl, path));
    if (init?.query) {
      for (const [key, value] of Object.entries(init.query)) {
        if (value !== undefined) url.searchParams.set(key, String(value));
      }
    }

    const headers = await this.buildHeaders(init?.headers);
    if (init?.body !== undefined && !headers.has("content-type")) {
      headers.set("content-type", "application/json");
    }

    const response = await this.fetchImpl(url.toString(), {
      ...init,
      method,
      headers,
      // `credentials: "include"` is required for the session-cookie path when
      // the SDK runs in a browser against a different subdomain. No effect in
      // Node / Workers fetch.
      credentials: init?.credentials ?? this.defaultCredentials,
    });

    let parsedBody: unknown = null;
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      parsedBody = await response.json();
    } else {
      const text = await response.text();
      parsedBody = text.length > 0 ? text : null;
    }

    if (!response.ok) {
      throw toApiError(parsedBody, response.status);
    }

    return schema.parse(parsedBody);
  }

  // — User/session probe —

  me(): Promise<MeResponse> {
    return this.request("GET", "/v1/me", meResponseSchema);
  }

  // — Cipher CRUD —

  async listCiphers(input: ListCiphersInput = {}): Promise<ListCiphersData> {
    const result = await this.request(
      "GET",
      "/v1/ciphers",
      successEnvelope(listCiphersDataSchema),
      { query: { cursor: input.cursor, limit: input.limit } },
    );
    return result.data;
  }

  async createCipher(input: CreateCipherInput): Promise<PublicCipher> {
    const result = await this.request(
      "POST",
      "/v1/ciphers",
      successEnvelope(cipherDataSchema),
      { body: JSON.stringify(input) },
    );
    return result.data.cipher as PublicCipher;
  }

  async getCipher(code: string): Promise<PublicCipher | CipherWithMapping> {
    const result = await this.request(
      "GET",
      `/v1/ciphers/${encodeURIComponent(code)}`,
      successEnvelope(cipherDataSchema),
    );
    return result.data.cipher;
  }

  async updateCipher(
    code: string,
    input: UpdateCipherInput,
  ): Promise<PublicCipher> {
    const result = await this.request(
      "PATCH",
      `/v1/ciphers/${encodeURIComponent(code)}`,
      successEnvelope(cipherDataSchema),
      { body: JSON.stringify(input) },
    );
    return result.data.cipher as PublicCipher;
  }

  async deleteCipher(code: string): Promise<DeletedCipherData> {
    const result = await this.request(
      "DELETE",
      `/v1/ciphers/${encodeURIComponent(code)}`,
      successEnvelope(deletedCipherDataSchema),
    );
    return result.data;
  }

  // — Encode / decode —

  async encode(input: EncodeInput): Promise<EncodeData> {
    const result = await this.request(
      "POST",
      "/v1/encode",
      successEnvelope(encodeDataSchema),
      { body: JSON.stringify(input) },
    );
    return result.data;
  }

  async decode(input: DecodeInput): Promise<DecodeData> {
    const result = await this.request(
      "POST",
      "/v1/decode",
      successEnvelope(decodeDataSchema),
      { body: JSON.stringify(input) },
    );
    return result.data;
  }

  // — API key management —

  async createApiKey(input: CreateApiKeyInput = {}): Promise<CreatedApiKey> {
    const result = await this.request(
      "POST",
      "/v1/api-keys",
      successEnvelope(createApiKeyDataSchema),
      { body: JSON.stringify(input) },
    );
    return result.data.api_key;
  }

  async listApiKeys(): Promise<PublicApiKey[]> {
    const result = await this.request(
      "GET",
      "/v1/api-keys",
      successEnvelope(listApiKeysDataSchema),
    );
    return (result.data as ListApiKeysData).api_keys;
  }

  async revokeApiKey(prefix: string): Promise<RevokedApiKeyData> {
    const result = await this.request(
      "DELETE",
      `/v1/api-keys/${encodeURIComponent(prefix)}`,
      successEnvelope(revokedApiKeyDataSchema),
    );
    return result.data;
  }

  // — Verification (Phase 04) —

  async createVerificationRequest(
    cipherCode: string,
    input: CreateVerificationInput = {},
  ): Promise<Verification> {
    const result = await this.request(
      "POST",
      `/v1/ciphers/${encodeURIComponent(cipherCode)}/verifications`,
      successEnvelope(verificationDataSchema),
      { body: JSON.stringify(input) },
    );
    return result.data.verification;
  }

  async decideVerification(
    cipherCode: string,
    requestId: string,
    input: DecideVerificationInput,
  ): Promise<Verification> {
    const result = await this.request(
      "POST",
      `/v1/ciphers/${encodeURIComponent(cipherCode)}/verifications/${encodeURIComponent(requestId)}/decide`,
      successEnvelope(verificationDataSchema),
      { body: JSON.stringify(input) },
    );
    return result.data.verification;
  }

  async listVerifications(
    cipherCode: string,
    input: ListVerificationsInput = {},
  ): Promise<ListVerificationsData> {
    const result = await this.request(
      "GET",
      `/v1/ciphers/${encodeURIComponent(cipherCode)}/verifications`,
      successEnvelope(listVerificationsDataSchema),
      { query: { cursor: input.cursor, limit: input.limit } },
    );
    return result.data;
  }

  // Open a long-lived WebSocket subscription against
  // `/v1/ciphers/:code/verifications/live` and invoke `onEvent` for each
  // server-sent frame. On abnormal close (any code other than 1000) the
  // stream automatically reconnects with exponential backoff, capped by
  // `reconnect.maxDelayMs` and `reconnect.maxAttempts`. Calling
  // `stream.close()` short-circuits the reconnect loop and emits a clean
  // 1000 close to the server.
  //
  // Browser WebSocket auth: the WHATWG WebSocket constructor cannot set
  // custom headers, so api-key (Bearer) auth is impossible in the browser.
  // Cookie-based session auth works automatically (same-origin requests
  // carry cookies). For agent / Node contexts where Bearer auth is needed,
  // pass a `webSocket` factory that wraps a header-capable implementation
  // (e.g. the `ws` npm package) and applies the Authorization header.
  streamVerifications(
    cipherCode: string,
    onEvent: (event: VerificationStreamEvent) => void,
    options: StreamVerificationsOptions = {},
  ): VerificationStream {
    const ctor = options.webSocket ?? this.webSocketImpl;
    if (!ctor) {
      throw new Error(
        "streamVerifications: no WebSocket implementation available — " +
          "pass `webSocket` in options or run in an environment with `globalThis.WebSocket`.",
      );
    }

    const url = buildWebSocketUrl(
      this.baseUrl,
      `/v1/ciphers/${encodeURIComponent(cipherCode)}/verifications/live`,
    );
    const reconnectConfig: Required<VerificationStreamReconnectOptions> = {
      initialDelayMs:
        options.reconnect?.initialDelayMs ?? DEFAULT_RECONNECT.initialDelayMs,
      maxDelayMs:
        options.reconnect?.maxDelayMs ?? DEFAULT_RECONNECT.maxDelayMs,
      maxAttempts:
        options.reconnect?.maxAttempts ?? DEFAULT_RECONNECT.maxAttempts,
    };
    const setTimeoutFn =
      options.setTimeout ??
      ((handler, ms) => globalThis.setTimeout(handler, ms));
    const clearTimeoutFn =
      options.clearTimeout ??
      ((handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>));

    let socket: WebSocketLike | null = null;
    let attempts = 0;
    let manualClose = false;
    let reconnectHandle: unknown = null;
    let closedFlag = false;

    const cancelPendingReconnect = (): void => {
      if (reconnectHandle !== null) {
        clearTimeoutFn(reconnectHandle);
        reconnectHandle = null;
      }
    };

    const connect = (): void => {
      if (manualClose) return;
      const ws = new ctor(url);
      socket = ws;

      ws.addEventListener("open", () => {
        // A successful open resets the backoff so the next abnormal close
        // starts the doubling sequence over from `initialDelayMs`.
        attempts = 0;
        options.onOpen?.();
      });

      ws.addEventListener("message", (event: MessageEvent) => {
        let raw: unknown;
        try {
          raw = JSON.parse(event.data as string);
        } catch (parseError) {
          options.onError?.(parseError);
          return;
        }
        const parsed = verificationStreamEventSchema.safeParse(raw);
        if (!parsed.success) {
          options.onError?.(parsed.error);
          return;
        }
        try {
          onEvent(parsed.data);
        } catch (handlerError) {
          // Bubble synchronous handler exceptions to the error sink so a
          // bad event handler doesn't silently swallow the frame.
          options.onError?.(handlerError);
        }
      });

      ws.addEventListener("error", (error: Event) => {
        options.onError?.(error);
      });

      ws.addEventListener("close", (event: CloseEvent) => {
        socket = null;
        if (manualClose) {
          closedFlag = true;
          options.onClosed?.();
          return;
        }
        // Code 1000 = normal closure. Anything else (including 1001 going
        // away, 1006 abnormal, server-side hard close) triggers reconnect.
        if (event.code === 1000) {
          closedFlag = true;
          options.onClosed?.();
          return;
        }
        attempts += 1;
        if (attempts > reconnectConfig.maxAttempts) {
          closedFlag = true;
          options.onClosed?.();
          return;
        }
        const delay = Math.min(
          reconnectConfig.initialDelayMs * 2 ** (attempts - 1),
          reconnectConfig.maxDelayMs,
        );
        reconnectHandle = setTimeoutFn(() => {
          reconnectHandle = null;
          connect();
        }, delay);
      });
    };

    connect();

    return {
      close: () => {
        if (manualClose) return;
        manualClose = true;
        cancelPendingReconnect();
        if (socket && socket.readyState !== 3 /* CLOSED */) {
          socket.close(1000, "client closed");
        } else {
          // Already torn down — surface `closed = true` immediately so
          // callers don't have to wait for the close event.
          closedFlag = true;
          options.onClosed?.();
        }
      },
      get closed() {
        return closedFlag;
      },
    };
  }
}

// Convert `https://api.example.com` → `wss://api.example.com` (and the
// `http:` → `ws:` analogue). Any other scheme passes through unchanged so
// the caller's `WebSocket` impl can decide what to do with it.
function buildWebSocketUrl(baseUrl: string, path: string): string {
  const joined = joinUrl(baseUrl, path);
  if (joined.startsWith("https://")) return `wss://${joined.slice("https://".length)}`;
  if (joined.startsWith("http://")) return `ws://${joined.slice("http://".length)}`;
  return joined;
}

function toApiError(body: unknown, status: number): CryptolectApiError {
  const structured = structuredErrorSchema.safeParse(body);
  if (structured.success) {
    return new CryptolectApiError({
      code: structured.data.error.code,
      message: structured.data.error.message,
      status,
      issues: structured.data.error.issues,
      body,
    });
  }

  const legacy = legacyErrorSchema.safeParse(body);
  if (legacy.success) {
    // Legacy 401 — map to `invalid_token` so callers can branch on a stable
    // code. The only routes still emitting this today are `requireSession`
    // (cookie missing) and `requireAuth` missing-cred fallback.
    return new CryptolectApiError({
      code: "invalid_token",
      message: legacy.data.error,
      status,
      body,
    });
  }

  // Unknown shape — surface as upstream_error so callers don't crash on a
  // malformed gateway response. Body is preserved for debugging.
  return new CryptolectApiError({
    code: "upstream_error",
    message: `Unexpected ${status} response from Cryptolect API`,
    status,
    body,
  });
}

// Re-export for callers that want to validate the error shape themselves
// (e.g., tests that call `errorBodySchema.parse(...)`).
export { errorBodySchema };
